import { SimpleEventDispatcher } from "strongly-typed-events";

const assets = {
  states: {
    locked: "door_locked.png",
    closed: "door_closed.png",
    opened: "door_open.png",
  },
  animations: {
    unlock: ["lock_opening.webp", 20 * 40] as [string, number],
    opening: ["door_opening.webp", 22 * 40] as [string, number],
    closing: ["door_closing.webp", 22 * 40] as [string, number],
  },
};

type States = keyof typeof assets.states;

async function preload_assets() {
  for (const key in assets["states"]) {
    const img = new Image();
    img.src = assets["states"][key as keyof (typeof assets)["states"]];
    await new Promise((resolve) => {
      img.onload = resolve;
    });
  }
  for (const key in assets["animations"]) {
    const img = new Image();
    img.src =
      assets["animations"][key as keyof (typeof assets)["animations"]][0];
    await new Promise((resolve) => {
      img.onload = resolve;
    });
  }
}

export class DoorController {
  current_state: States = "locked";
  door_container: HTMLDivElement;
  door_element: HTMLImageElement;
  is_animating: boolean = false;
  user_hovering: boolean = false;
  user_identifiable: boolean = false;
  door_clicked = new SimpleEventDispatcher<void>();

  static async preload() {
    await preload_assets();
  }

  constructor() {
    const door_container = document.querySelector("#door");
    if (!door_container) {
      throw new Error("Door container not found");
    }
    this.door_element = document.createElement("img");
    door_container.append(this.door_element);
    this.door_container = door_container as HTMLDivElement;
    door_container.addEventListener("mouseenter", () => {
      this.user_hovering = true;
      if (this.current_state !== "locked") {
        this.transition(this.current_state, "opened");
      }
    });
    door_container.addEventListener("mouseleave", () => {
      this.user_hovering = false;
      if (this.current_state !== "locked") {
        this.transition(this.current_state, "closed");
      }
    });
    door_container.addEventListener("click", () => {
      this.door_clicked.dispatch();
    });
    this.door_element.src = assets.states.locked;
    this.door_container.className = "door--locked";
  }

  async transition(fromState: States, toState: States) {
    if (this.is_animating) return;
    this.is_animating = true;

    const [animation_url, animation_time] = this.getTransitionAnimation(
      fromState,
      toState,
    );

    this.door_element.src = animation_url;

    await new Promise((resolve) => setTimeout(resolve, animation_time));

    this.door_element.src = assets.states[toState];

    this.current_state = toState;
    this.is_animating = false;

    if (this.user_hovering && toState === "closed") {
      this.transition(this.current_state, "opened");
    }

    if (!this.user_hovering && toState === "opened") {
      this.transition(this.current_state, "closed");
    }
  }

  hide_door() {
    this.door_container.className = "hidden";
  }

  set_user_identifiable(identifiable: boolean) {
    if (!this.user_identifiable && identifiable) {
      this.transition(this.current_state, "closed");
    }
    this.user_identifiable = identifiable;
    if (identifiable) {
      this.door_container.className = "";
    }
  }

  getTransitionAnimation(fromState: States, toState: States): [string, number] {
    if (fromState === "locked" && toState === "closed")
      return assets.animations.unlock;
    if (fromState === "closed" && toState === "opened")
      return assets.animations.opening;
    if (fromState === "opened" && toState === "closed")
      return assets.animations.closing;

    return ["", 0];
  }
}
